import CustomModal from "components/common/modal";
import TableWrapper from "components/common/table-wrapper";
import Tooltip from "components/common/tooltip";
import AddEditReleaseForm from "components/release/AddEditReleaseForm";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import { Pencil, Plus } from "phosphor-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchLatestRelease } from "store/reducers/latestRelease";
import {
  capitalizeName,
  highlightFirstMatch,
  truncateString,
} from "utils/common";
import { createRelease, getReleases, updateRelease } from "./api";

let timeout;
const COLUMNS = [
  { columnKey: "version", label: "Version", isSortable: true },
  { columnKey: "release_type", label: "Type", isSortable: true },
  { columnKey: "created_at", label: "Date Created", isSortable: true },
  { columnKey: "features", label: "Features", isSortable: true },
  {
    columnKey: "bugs",
    label: "Bugs",
    isSortable: false,
  },
];

const Releases = () => {
  const dispatch = useDispatch();
  const [releaseList, setReleaseList] = useState([]);
  const [meta, setMeta] = useState(initialMetaForTable);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [releaseToEdit, setReleaseToEdit] = useState(undefined);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  const debounceFn = (callback, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(callback, delay);
  };

  const handleSetSearchQuery = (value) => {
    let newMetaObj = { ...meta, search: value };
    setMeta(newMetaObj);
    debounceFn(() => {
      fetchReleaseList(newMetaObj);
    }, DEBOUNCE_DELAY);
  };

  const handlePageChange = (value) => {
    let newMetaObj = { ...meta, page: value };
    setMeta(newMetaObj);
    debounceFn(() => {
      fetchReleaseList(newMetaObj);
    }, DEBOUNCE_DELAY);
  };

  const fetchReleaseList = async (metaObj = meta) => {
    setLoading(true);
    try {
      const result = await getReleases({
        ...metaObj,
        key: sortConfig.key,
        order: sortConfig.direction,
      });
      if (result?.releases) {
        setReleaseList(result?.releases);
        setMeta((prev) => ({
          ...prev,
          perPage: result?.meta?.per_page,
          totalCount: result?.meta?.total_count,
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseModal = () => {
    setIsModalVisible((prev) => !prev);
    if (!isModalVisible) setReleaseToEdit(undefined);
  };

  const handleRelease = async (payload) => {
    let { id, ...data } = payload;
    const result = await (!!id ? updateRelease(id, data) : createRelease(data));
    if (result?.meta?.status === 200) {
      handleReleaseModal();
      fetchReleaseList();
      dispatch(fetchLatestRelease());
    }
  };
  const openReleaseModal = (release = undefined) => {
    setReleaseToEdit(release);
    setIsModalVisible(true);
  };

  useEffect(() => {
    fetchReleaseList();
  }, [sortConfig.direction, sortConfig.key]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const SortableTableHeader = ({
    columnKey,
    label,
    sortConfig,
    onSortChange,
  }) => {
    const isSorting = sortConfig.key === columnKey;
    const isAscending = isSorting && sortConfig.direction === "asc";
    const isDescending = isSorting && sortConfig.direction === "desc";

    const handleSort = () => {
      if (isAscending) {
        onSortChange(columnKey, "desc");
      } else {
        onSortChange(columnKey, "asc");
      }
    };

    return (
      <th onClick={handleSort} style={{ cursor: "pointer" }}>
        <div className="d-flex position-relative">
          <div className="d-flex flex-column cursor-pointer me-2 position-absolute top-50  end-100 translate-middle-y ">
            {/* <CaretUp size={12} color={isAscending ? '#000000' : '#00000050'} weight="fill" />
            <CaretDown color={isDescending ? '#000000' : '#00000050'} size={12} weight="fill" /> */}
          </div>
          <div>{label}</div>
        </div>
      </th>
    );
  };

  return (
    <>
      <div className="main-contents">
        <h1 className="heading-h5">Releases</h1>
        <TableWrapper
          searchPlaceholder="Search Release Number"
          setSearhQuery={handleSetSearchQuery}
          searchValue={meta?.search}
          totalListCount={meta?.totalCount || 0}
          pageSize={meta?.perPage}
          currentPage={meta?.page}
          onPageChange={handlePageChange}
          isLoading={loading}
          actionButtons={[
            {
              label: "Add Release",
              classes: "primary-btn",
              icon: <Plus size={24} className="me-3" />,
              handleClick: () => openReleaseModal(),
            },
          ]}
        >
          <div className="container-fluid">
            <div className="table-responsive">
              <table className="table table-rows">
                <thead className="table-header">
                  <tr>
                    {COLUMNS.map((column) => (
                      <SortableTableHeader
                        key={column.columnKey}
                        columnKey={column.columnKey}
                        label={column.label}
                        isSortable={column.isSortable}
                        sortConfig={sortConfig}
                        onSortChange={handleSort}
                      />
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {releaseList?.map((release, index) => (
                    <tr key={index}>
                      <td>
                        {highlightFirstMatch(
                          capitalizeName(release.version),
                          meta.search
                        )}
                      </td>
                      <td>
                        {highlightFirstMatch(release.release_type, meta.search)}
                      </td>
                      <td>{release.created_at}</td>
                      <td>
                        <Tooltip
                          id="Features"
                          title={
                            <ul className="text-start">
                              {release.features_list?.map((feat) => (
                                <li key={feat}>{feat}</li>
                              ))}
                            </ul>
                          }
                        >
                          <span>{truncateString(release?.features)}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          id="Bugs"
                          title={
                            <ul className="text-start">
                              {release.bugs_list?.map((bug) => (
                                <li key={bug}>{bug}</li>
                              ))}
                            </ul>
                          }
                        >
                          <span>{truncateString(release?.bugs)}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title="Edit">
                          <Pencil
                            size={24}
                            className="opacity-50"
                            style={{ cursor: "pointer" }}
                            onClick={() => openReleaseModal(release)}
                          />
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TableWrapper>
        {isModalVisible && (
          <CustomModal
            size="sm"
            show
            onHide={handleReleaseModal}
            heading={`${releaseToEdit ? "Edit" : "Add"} Release`}
          >
            <AddEditReleaseForm
              handleSubmit={handleRelease}
              handleClose={handleReleaseModal}
              releaseToEdit={releaseToEdit}
            />
          </CustomModal>
        )}
      </div>
    </>
  );
};

export default Releases;
